"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: MapPin,
  title: "Hauptsitz",
  content: "An den Kreutzmorgen 9\n507373 Köln",
  link: null
}, {
  icon: Phone,
  title: "Telefon",
  content: "+49 157 31141389",
  link: "tel:+493012345678"
}, {
  icon: Mail,
  title: "E-Mail\ninfo@chrotechs.com\xA0",
  content: "info@chrotechs.com",
  link: "mailto:info@chrotech.de"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-primary/20 bg-primary/5 shadow-lg">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-lg bg-primary/10 p-3 text-primary">
            <Send className="size-6" />
          </div>
          <h3 className="font-default mb-2 text-xl font-semibold text-foreground">Nachricht gesendet!</h3>
          <p className="mb-6 text-muted-foreground">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            Neue Nachricht senden
          </Button>
        </CardContent>
      </Card>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name ist erforderlich",
        minLength: {
          value: 2,
          message: "Name muss mindestens 2 Zeichen lang sein"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Vollständiger Name</FormLabel>
              <FormControl>
                <Input placeholder="Max Mustermann" className="bg-secondary/50 border-border/50 focus:border-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-Mail ist erforderlich",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte geben Sie eine gültige E-Mail-Adresse ein"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">E-Mail-Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="max@beispiel.de" className="bg-secondary/50 border-border/50 focus:border-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Nachricht ist erforderlich",
      minLength: {
        value: 10,
        message: "Nachricht muss mindestens 10 Zeichen lang sein"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">Ihre Nachricht</FormLabel>
            <FormControl>
              <Textarea placeholder="Wie können wir Ihnen heute helfen?" className="min-h-[150px] resize-y bg-secondary/50 border-border/50 focus:border-primary rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity font-default font-semibold tracking-wide" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Wird gesendet...
          </> : <>
            <Send className="mr-2 size-4" />
            Nachricht senden
          </>}
      </Button>
    </Form>;
}
export default function ContactInformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="254146" id="gemeinsam-wachsen" className="bg-background text-foreground py-16 md:py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.03] overflow-hidden">
        <span className="text-[20vw] font-bold tracking-tighter font-default">CHROTECH</span>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-12 max-w-2xl md:mb-16">
            <h2 className="font-default mb-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
              Gemeinsam <span className="text-primary">Wachsen</span>
            </h2>
            <p className="text-xl text-muted-foreground font-default">
              Haben Sie Fragen oder möchten Sie ein Projekt starten? Nutzen Sie unser Formular oder kontaktieren Sie uns direkt über WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
            {}
            <div className="lg:col-span-7 xl:col-span-8">
              <Card className="border-border/50 bg-card/40 shadow-xl backdrop-blur-xl rounded-2xl">
                <CardContent className="p-6 sm:p-8">
                  <FormProvider formKey="growth_form_home" formId="growth_form_home" sectionName="Contact Information" defaultValues={{
                  name: "",
                  email: "",
                  message: ""
                }} submitText="Nachricht senden" successMessage="Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.">
                    <ContactForm />
                  </FormProvider>
                </CardContent>
              </Card>
            </div>

            {}
            <div className="lg:col-span-5 xl:col-span-4">
              <div className="flex flex-col gap-8 rounded-2xl bg-secondary/20 p-8 shadow-lg border border-border/30 backdrop-blur-md">
                <h3 className="font-default text-2xl font-bold text-foreground">
                  Quick Connect
                </h3>
                
                <div className="flex flex-col gap-6">
                  {contactDetails.map((detail, i) => <div data-index={i} key={i} className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary">
                        <detail.icon className="size-5" />
                      </div>
                      <div>
                        <h4 className="font-default mb-1 font-semibold text-foreground">
                          {detail.title}
                        </h4>
                        {detail.link ? <Link to={detail.link} className="text-muted-foreground transition-colors hover:text-primary font-default">
                            {detail.content}
                          </Link> : <p className="whitespace-pre-line text-muted-foreground font-default">
                            {detail.content}
                          </p>}
                      </div>
                    </div>)}
                </div>

                <div className="mt-4 flex flex-col items-center gap-4 rounded-xl bg-background/40 p-6 text-center shadow-md border border-primary/20">
                  <h4 className="font-default mb-2 font-semibold text-foreground">WhatsApp & QR</h4>
                  <div className="mb-4 p-2 bg-white rounded-lg border-2 border-primary">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1669233/img/chrotech_whatsapp_qr.png" alt="WhatsApp QR Code" className="size-32 object-contain" src="https://good-piranha.10web.cloud/wp-content/uploads/2026/05/chrotech_whatsapp_qr.png" />
                  </div>
                  <Button asChild className="w-full rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white font-default font-bold">
                    <Link to="https://wa.me/491639538737">
                      WhatsApp Chat starten
                    </Link>
                  </Button>
                </div>

                <div className="mt-2 text-center">
                  <p className="text-sm text-muted-foreground font-default">24/7 Monitoring &amp; Notfall-Support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}